import { Link as RouterLink } from 'react-router-dom';
import { ICellRendererParams } from "ag-grid-community";

import { BodySmall, Box, Link, Modal, ModalFooter, ModalHeader } from "@parspec/pixel";

import { useDeleteFolderForProject, useDeleteUploadedFilesForProject, useDownloadDocument } from 'src/features/Project/queries';
import { getProxyFileUrl } from "../utils/pdfUtils";
import { ProjectFileRow, ProjectFolderRow } from './types';
import { AxiosResponse } from 'axios';
import { callProxyURL } from 'src/features/BOM/FinalDocuments/utils/downloadFile';
import { SnackbarMessages } from 'src/features/BOM/FinalDocuments/constants';
import { DOWNLOAD_FAILED_MSG } from '../constants';
import { useProjectsTableContext } from './context';

const DATA = {
    FILE: {
        title: "Are you sure you want to delete this file?",
        description: "Deleting this file will remove it from this project for everyone.",
    },
    FOLDER: {
        title: "Are you sure you want to delete this folder?",
        description: "Deleting this folder will remove it from this project for everyone.",
    },
} as const;

const bodySmallSX = { lineHeight: "1.25rem" };

export function DeleteConfirmationModal({
    open,
    data,
    onClose,
    onDeleteSuccess,
}: {
    open: boolean;
    data: { entityId: number, projectId: number, isFolder: boolean };
    onClose: () => void;
    onDeleteSuccess?: (message: string) => void;
}) {
    const { isFolder, entityId, projectId } = data;
    const { mutateAsync: deleteUploadedFile, isLoading: isDeleting } = useDeleteUploadedFilesForProject();
    const { mutateAsync: deleteFolder, isLoading: isDeletingFolder } = useDeleteFolderForProject();

    const isDeletingItem = isFolder ? isDeletingFolder : isDeleting;

    const onDelete = async () => {
        try {
            const response = isFolder
                ? await deleteFolder({ projectId, folderId: entityId })
                : await deleteUploadedFile({ projectId, documentId: entityId });
            const { message } = response.data;
            if (onDeleteSuccess) onDeleteSuccess(message);
            onClose();
        } catch (error) {
            // Note: error snackbar already handled in the response interceptor
        }
    };

    const header = <ModalHeader title="Delete" onClose={onClose} />
    const footer = (
        <ModalFooter
            onReject={onClose}
            onAccept={onDelete}
            continueButtonLabel="Delete"
            cancelButtonLabel="Cancel"
            continueButtonColor="error"
            isLoading={isDeletingItem}
        />
    );

    const { title, description } = DATA[isFolder ? 'FOLDER' : 'FILE'];

    return (
        <Modal header={header} footer={footer} open={open} borderRadius="8px">
            <Box width={480} flexDirection="column" display="flex">
                <BodySmall fontWeight={700} color="text.secondary" sx={bodySmallSX}>{title}</BodySmall>
                <BodySmall color="text.secondary" sx={bodySmallSX}>{description}</BodySmall>
            </Box>
        </Modal>
    );
}

const ellipsisNameSX = {
    display: 'inline',
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: '0.5ch'
}

const noShrinkSX = { flexShrink: 0 };
export const EmptyFolderRenderer = (props: ICellRendererParams<ProjectFolderRow>) => {
    if (!props.data) return null;

    return (
        <Box display="flex" alignItems="center" width="100%">
            <BodySmall limit={false} sx={ellipsisNameSX}>
                {props.data.name}
            </BodySmall>
            <BodySmall sx={noShrinkSX} limit={false}>
                ({props.data.document_count})
            </BodySmall>
        </Box>
    )
}

export const FieldNameRenderer = (props: ICellRendererParams<ProjectFileRow>) => {

    const { setSnackbarOpen } = useProjectsTableContext()
    const { mutate: downloadDocument } = useDownloadDocument();

    if (!props.data) return null;

    const { id, file_type, file_name, project: projectId } = props?.data;


    const handleDownloadFile = () => {
        downloadDocument(
            { projectId, documentId: id },
            {
                onSuccess: async (data: AxiosResponse) => {
                    await callProxyURL(data?.data.s3_url, file_type, file_name);
                    setSnackbarOpen(SnackbarMessages.DOWNLOAD_SUCCESS);
                },
                onError: () => {
                    setSnackbarOpen(DOWNLOAD_FAILED_MSG);
                }
            }
        );
    };

    if (file_type === 'pdf') {
        return (
            <>
                <Link to={getProxyFileUrl(props.data.s3_url)} target="_blank" rel="noopener noreferrer" underline="hover" component={RouterLink}>
                    {props.data.file_name + '.' + props.data.file_type}
                </Link>
            </>
        );
    }

    // some file extensions we not getting added to the file name if user downloads the file via clicking the name
    // Reported for bug: QQ-5028
    return (
        <>
            <Link onClick={handleDownloadFile} to="" rel="noopener noreferrer" underline="hover" component={RouterLink}>
                {props.data.file_name + '.' + props.data.file_type}
            </Link>
        </>
    );
};
